import { Alert, Button, Stack, Typography } from '@mui/material';
import { LinkOffOutlined } from '@mui/icons-material';
import moment from 'moment';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { LoadingPage } from '../../../components/pages/loading-page';
import { InformationBox } from '../../../components/presentation-components/miscellaneous-components/information-box';
import { ShallowFormDisplay } from '../../../components/presentation-components/form-field-components/general/shallow-form-display';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import ThirdPartyConnectionDetailsService from '../shared-services/third-party-connection-details/third-party-connection.service';
import { IThirdPartyConnectionDetails } from '../shared-services/third-party-connection-details/third-party-connection-details.model';
import CloudEmissionsService from '../shared-services/cloud-emissions/cloud-emissions.service';
import { CloudEmissionsProvider, ICloudConnectionStatus } from '../shared-services/cloud-emissions/cloud-emissions.model';
import { ConnectionButton } from '../shared-components/components/connection-button';
import { AwsConnectPanel } from './aws-connect-panel';
import { GcpConnectPanel } from './gcp-connect-panel';
import { CloudSetupCard } from './cloud-setup-card';
import { CloudMonthsPanel } from './cloud-months-panel';

/**
 * The details page for a cloud carbon connector: connect, confirm setup, then sync and book
 * the vendor's monthly figures. Replaces the ledger connectors' generic Details, which is
 * built around companies and a chart of accounts that a carbon report does not have.
 */
export const CloudEmissionsDetails: FunctionComponent<{ provider: CloudEmissionsProvider }> = ({ provider }) => {
  const cloudEmissionsService = new CloudEmissionsService();
  const thirdPartyConnectionDetailsService = new ThirdPartyConnectionDetailsService();
  const { userDetails } = useContext(UserDetailsContext);
  const userNotificationsContext = useContext(UserNotificationsContext);
  const isAdmin = userDetails?.roles?.includes('admin') ?? false;

  const [status, setStatus] = useState<ICloudConnectionStatus | null>(null);
  const [oauthDetails, setOauthDetails] = useState<IThirdPartyConnectionDetails>();
  const [pageReady, setPageReady] = useState(false);
  const [errorText, setErrorText] = useState('');

  const loadStatus = () => cloudEmissionsService.getStatus(provider)
    .then(response => {
      setStatus(response);
      setErrorText('');
    })
    .catch(err => {
      console.error('Error loading the cloud connection:', err);
      setErrorText('This integration could not be loaded. Please try again.');
    })
    .finally(() => setPageReady(true));

  useEffect(() => {
    setPageReady(false);
    loadStatus();
    if (provider === 'azure-carbon') {
      thirdPartyConnectionDetailsService.getConnection(provider).then(setOauthDetails).catch(() => setOauthDetails(undefined));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  const disconnect = async () => {
    const confirmed = await userNotificationsContext.openDialog({
      active: true,
      title: 'Disconnect?',
      message: 'Months already booked stay in your footprint. No new figures are fetched until you connect again.',
      buttonCopy: 'Disconnect',
      cancelButtonCopy: 'Cancel',
    });
    if (!confirmed) return;
    cloudEmissionsService.disconnect(provider)
      .then(response => {
        userNotificationsContext.openSnackbar({ open: true, message: response.message, backgroundColor: 'text.primary' });
        loadStatus();
      })
      .catch(err => userNotificationsContext.openSnackbar({
        open: true,
        message: err?.response?.data?.message || 'The connection could not be removed. Please try again.',
        backgroundColor: 'text.primary',
      }));
  };

  const lastSync = status?.connection?.settings.lastSync;

  return (
    <LoadingPage pageReady={pageReady}>
      <>
        {errorText && <InformationBox icon={IconName.ErrorOutline} text={errorText} />}
        {status && !status.configured && (
          <InformationBox
            icon={IconName.ErrorOutline}
            text='This integration is not available yet. Contact support to have it switched on for your account.'
          />
        )}
        {status && status.configured && (
          <Stack spacing={3}>
            <Stack>
              <ShallowFormDisplay
                name='Connection'
                chip={status.connected ? { label: 'Connected', color: 'success' } : { label: 'Not set up', color: 'default' }}
                isTopRow
                isBottomRow={!status.connected}
              />
              {status.connected && status.connection?.roleArn && (
                <ShallowFormDisplay name='IAM role' value={status.connection.roleArn} />
              )}
              {status.connected && status.connection?.bigQueryTable && (
                <ShallowFormDisplay name='BigQuery table' value={status.connection.bigQueryTable} />
              )}
              {status.connected && (
                <ShallowFormDisplay
                  name='Last sync'
                  value={lastSync ? `${moment(lastSync.at).format('D MMM YYYY, HH:mm')}${lastSync.source === 'worker' ? ' (scheduled)' : ''}` : 'Not synced yet'}
                  isBottomRow
                />
              )}
            </Stack>

            {status.connected && provider === 'aws-sustainability' && status.connectResult?.accountCount === 0 && (
              <Alert severity='info'>
                The role works. AWS has no carbon figures for this account yet; they appear once there is usage history.
              </Alert>
            )}

            {status.connected && provider === 'google-cloud-carbon' && status.connectResult?.projectCount === 0 && (
              <Alert severity='info'>
                The table can be read, but it has no carbon figures yet. Google exports each month on the 15th of the
                following month; a backfill brings in earlier months.
              </Alert>
            )}

            {!status.connected && provider === 'aws-sustainability' && (
              <AwsConnectPanel status={status} isAdmin={isAdmin} onUpdated={setStatus} />
            )}

            {!status.connected && provider === 'google-cloud-carbon' && (
              <GcpConnectPanel status={status} isAdmin={isAdmin} onUpdated={setStatus} />
            )}

            {!status.connected && provider === 'azure-carbon' && (
              <Stack spacing={2}>
                <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px' }}>
                  Sign in with an Azure account that has Carbon Optimization Reader (or Reader) on the subscriptions
                  you want to report. We only read carbon figures; nothing in your Azure tenant is changed.
                </Typography>
                {isAdmin
                  ? oauthDetails && (
                    <Stack direction='row' justifyContent='flex-end'>
                      <ConnectionButton isConnected={false} connectionDetails={oauthDetails} provider={provider} />
                    </Stack>
                  )
                  : <Typography variant='body2' sx={{ color: 'text.secondary' }}>Ask an admin in your organisation to connect Azure.</Typography>}
              </Stack>
            )}

            {status.connected && <CloudSetupCard status={status} isAdmin={isAdmin} onUpdated={setStatus} />}
            {status.connected && <CloudMonthsPanel status={status} isAdmin={isAdmin} onStatusStale={loadStatus} />}

            {status.connected && isAdmin && (
              <Stack direction='row' justifyContent='flex-end'>
                <Button variant='outlined' startIcon={<LinkOffOutlined fontSize='small' />} onClick={disconnect}>
                  <Typography sx={{ pl: 0.5 }}>Disconnect</Typography>
                </Button>
              </Stack>
            )}
          </Stack>
        )}
      </>
    </LoadingPage>
  );
};
